import { useState, useEffect, useRef } from 'react'
import { getOtMensajes, escribirOtMensaje, downloadOtMensajeAdjunto, verOtMensajeAdjunto } from '../../api/client'
import { formatFechaHora } from '../unidades/tipos'
import MentionTextarea from '../menciones/MentionTextarea'
import TextoConMenciones from '../menciones/TextoConMenciones'

const MAX_ARCHIVOS = 10
const MAX_BYTES = 10 * 1024 * 1024
const esImagen = (a) => (a.mime_type || '').startsWith('image/')
const kb = (n) => `${Math.max(1, Math.round(n / 1024))} KB`
const iniciales = (n) => (n || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)

// The OT's conversation (inside its panel): anyone who can open the OT reads
// and writes notes, with files -- picked, dropped on the box or pasted.
// Messages are neither edited nor deleted: the conversation is the record.
export default function Conversacion({ otId, t, onNuevo }) {
  const [mensajes, setMensajes] = useState(null)
  const [texto, setTexto] = useState('')
  const [archivos, setArchivos] = useState([])
  const [enviando, setEnviando] = useState(false)
  const [encima, setEncima] = useState(false)
  const [error, setError] = useState('')
  const fin = useRef(null)
  const input = useRef(null)

  useEffect(() => {
    let vivo = true
    getOtMensajes(otId).then(ms => { if (vivo) setMensajes(ms) }).catch(() => { if (vivo) setError(t('ot.chat.cargaFallo')) })
    return () => { vivo = false }
  }, [otId, t])
  useEffect(() => { fin.current?.scrollIntoView({ block: 'nearest' }) }, [mensajes?.length])

  const agregar = (lista) => {
    const nuevos = Array.from(lista || []).filter(f => f.size > 0)
    const grandes = nuevos.filter(f => f.size > MAX_BYTES)
    if (grandes.length) setError(t('ot.chat.muyGrande', { lista: grandes.map(f => f.name).join(', ') }))
    else setError('')
    setArchivos(xs => [...xs, ...nuevos.filter(f => f.size <= MAX_BYTES)].slice(0, MAX_ARCHIVOS))
  }
  const enviar = async () => {
    if (enviando || (!texto.trim() && !archivos.length)) return
    setEnviando(true); setError('')
    try {
      const m = await escribirOtMensaje(otId, texto.trim(), archivos)
      setMensajes(ms => [...(ms || []), m])
      setTexto(''); setArchivos([])
      onNuevo?.()
    } catch (err) {
      setError(err.response?.data?.detail?.message || t('ot.chat.envioFallo'))
    }
    setEnviando(false)
  }

  return (
    <section>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
        {t('ot.chat.titulo')}{mensajes?.length ? <span className="ml-1 text-gray-400 normal-case">· {mensajes.length}</span> : null}
      </h3>
      <div className="rounded-lg border border-gray-200 bg-gray-50/60">
        <div className="max-h-80 overflow-y-auto px-3 py-2 space-y-3">
          {!mensajes ? <p className="text-xs text-gray-400 py-2">{error || t('ot.chat.cargando')}</p>
            : !mensajes.length ? <p className="text-xs text-gray-400 py-2 text-center">{t('ot.chat.vacia')}</p>
              : mensajes.map(m => <Mensaje key={m.id} m={m} otId={otId} t={t} onError={setError} />)}
          <div ref={fin} />
        </div>
        <div
          onDragOver={e => { if (Array.from(e.dataTransfer?.types || []).includes('Files')) { e.preventDefault(); e.stopPropagation(); setEncima(true) } }}
          onDragLeave={() => setEncima(false)}
          onDrop={e => { if (!e.dataTransfer?.files?.length) return; e.preventDefault(); e.stopPropagation(); setEncima(false); agregar(e.dataTransfer.files) }}
          className={`border-t border-gray-200 bg-white rounded-b-lg p-2 ${encima ? 'ring-2 ring-inset ring-indigo-400 bg-indigo-50' : ''}`}>
          {archivos.length > 0 && (
            <ul className="flex flex-wrap gap-1.5 mb-2">
              {archivos.map((f, n) => (
                <li key={`${f.name}-${n}`} className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-0.5 text-[11px] text-gray-700">
                  📎 <span className="max-w-[16ch] truncate">{f.name}</span>
                  <button type="button" onClick={() => setArchivos(xs => xs.filter((_, i) => i !== n))}
                    className="text-gray-400 hover:text-red-500" aria-label={t('ot.chat.quitar')}>×</button>
                </li>
              ))}
            </ul>
          )}
          <MentionTextarea rows={2} className="input resize-none text-sm" value={texto} placeholder={encima ? t('ot.chat.suelta') : t('ot.chat.placeholder')}
            onChange={setTexto}
            onPaste={e => { if (e.clipboardData?.files?.length) { e.preventDefault(); agregar(e.clipboardData.files) } }}
            onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); enviar() } }} />
          <div className="mt-1.5 flex items-center gap-2">
            <input ref={input} type="file" multiple className="hidden" onChange={e => { agregar(e.target.files); e.target.value = '' }} />
            <button type="button" onClick={() => input.current?.click()} disabled={archivos.length >= MAX_ARCHIVOS}
              className="text-xs font-medium text-gray-600 border border-gray-200 rounded-md px-2.5 py-1 hover:bg-gray-50 disabled:opacity-40">
              📎 {t('ot.chat.adjuntar')}
            </button>
            <span className="text-[11px] text-gray-400 hidden sm:inline">{t('ot.chat.ayuda')}</span>
            <button type="button" onClick={enviar} disabled={enviando || (!texto.trim() && !archivos.length)}
              className="ml-auto text-xs font-medium bg-gray-900 text-white rounded-md px-3 py-1 hover:bg-gray-800 disabled:opacity-40">
              {enviando ? t('ot.chat.enviando') : t('ot.chat.enviar')}
            </button>
          </div>
          {error && mensajes && <p className="text-xs text-red-600 mt-1">{error}</p>}
        </div>
      </div>
    </section>
  )
}

function Mensaje({ m, otId, t, onError }) {
  return (
    <div className={`flex gap-2 ${m.propio ? 'flex-row-reverse' : ''}`}>
      <span className={`h-7 w-7 shrink-0 rounded-full flex items-center justify-center text-[10px] font-semibold ${
        m.propio ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'}`}>{iniciales(m.autor_nombre)}</span>
      <div className={`max-w-[80%] min-w-0 ${m.propio ? 'items-end text-right' : ''} flex flex-col`}>
        <p className="text-[11px] text-gray-500">
          <span className="font-medium text-gray-700">{m.propio ? t('ot.chat.tu') : (m.autor_nombre || '—')}</span> · {formatFechaHora(m.creado_en)}
        </p>
        {m.texto && (
          <p className={`mt-0.5 inline-block whitespace-pre-wrap break-words rounded-lg px-2.5 py-1.5 text-sm text-left ${
            m.propio ? 'bg-indigo-600 text-white' : 'bg-white border border-gray-200 text-gray-800'}`}><TextoConMenciones texto={m.texto} oscuro={m.propio} /></p>
        )}
        {m.adjuntos.length > 0 && (
          <div className={`mt-1 flex flex-wrap gap-1.5 ${m.propio ? 'justify-end' : ''}`}>
            {m.adjuntos.map(a => esImagen(a)
              ? <Miniatura key={a.id} a={a} otId={otId} mid={m.id} onError={() => onError(t('ot.chat.descargaFallo'))} />
              : (
                <button key={a.id} type="button" title={a.filename}
                  onClick={() => downloadOtMensajeAdjunto(otId, m.id, a).catch(() => onError(t('ot.chat.descargaFallo')))}
                  className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-[11px] text-gray-700 hover:border-indigo-300 hover:text-indigo-700">
                  📄 <span className="max-w-[20ch] truncate">{a.filename}</span><span className="text-gray-400">{kb(a.size_bytes)}</span>
                </button>
              ))}
          </div>
        )}
      </div>
    </div>
  )
}

// Images show as a thumbnail; a click downloads the original.
function Miniatura({ a, otId, mid, onError }) {
  const [url, setUrl] = useState(null)
  useEffect(() => {
    let vivo = true, creada = null
    verOtMensajeAdjunto(otId, mid, a).then(u => { creada = u; if (vivo) setUrl(u); else URL.revokeObjectURL(u) }).catch(() => {})
    return () => { vivo = false; if (creada) URL.revokeObjectURL(creada) }
  }, [otId, mid, a])
  return (
    <button type="button" title={a.filename} onClick={() => downloadOtMensajeAdjunto(otId, mid, a).catch(onError)}
      className="block h-20 w-20 overflow-hidden rounded-md border border-gray-200 bg-white hover:border-indigo-300">
      {url ? <img src={url} alt={a.filename} className="h-full w-full object-cover" />
        : <span className="flex h-full items-center justify-center text-[11px] text-gray-400">🖼</span>}
    </button>
  )
}
